import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';

import type { Dispatch } from '@avalara/types/actions';

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Pages } from '../../../../../types/offers';
import FormInput from '../../../containers/shared/forms/form-input';
import FormSelect from '../../../containers/shared/forms/form-select';
import { isFormIncomplete } from '../../../lib/shared-signup';
import { goToNextOfferStep } from '../../../lib/side-effects/offer-flow';
import { stepNames } from '../../../lib/side-effects/progress-bar';
import { selectState } from '../../../selectors';
import { isEmbeddedSelector } from '../../../selectors/offer';
import { setCurrentOfferPage } from '../../../store/offer';
import { setAdditionalRequiredFields } from '../../../store/shared-signup';
import { useFetchRegionsAndCountries } from '../../shared/hooks';
import SignInLink from '../sign-in-link';
import { FormContainer } from '../styled/forms';

type Props = {
  flow?: string;
  shouldFetchRegionsAndCountries?: boolean;
};

const Company = ({ flow, shouldFetchRegionsAndCountries }: Props) => {
  const dispatch = useDispatch<Dispatch>();
  const isEmbedded = useSelector(isEmbeddedSelector);
  const history = useHistory();

  const {
    offer: { allowedUsers, offerFlow },
    sharedSignup,
  } = useSelector(selectState);

  const emeaCompanyField = 'emeaCompanyType';
  const disabled = isFormIncomplete(sharedSignup);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Company));
    dispatch(setProgressBarCurrentStep(stepNames.companyDetails));
    if (offerFlow === 'ioss') dispatch(setAdditionalRequiredFields([emeaCompanyField]));
  }, []);

  const { formattedCountries, formattedRegions } = useFetchRegionsAndCountries(shouldFetchRegionsAndCountries);

  const emeaCompanyTypes = [{ value: 'Business' }, { value: 'Sole Trader' }];

  const handleClick = () => {
    if (offerFlow) {
      dispatch(goToNextOfferStep({ history }));
    } else if (flow) {
      history.push({ pathname: `/${flow}/payment`, search: window.location.search });
    }
  };

  return (
    <FormContainer isEmbedded={isEmbedded}>
      <h1>{t('client.headings.tell-us-more-about-your-company')}</h1>
      {allowedUsers !== 'NEW' && <SignInLink />}
      <form aria-label={t('client.accessibility.form-for-company-details')}>
        {offerFlow === 'ioss' && (
          <SRow>
            <SCol span='6' className='pad-right-sm pad-top-sm pad-bottom-sm'>
              <label htmlFor='company-type'>{t('client.labels.company-type')}</label>
              <FormSelect
                fieldName={emeaCompanyField}
                fieldValues={emeaCompanyTypes}
                validateDuringInput={true}
                addPadding={true}
              />
            </SCol>
          </SRow>
        )}
        <SRow>
          <SCol span='6' className='pad-right-sm'>
            <label htmlFor='street1'>{t('client.labels.primary-business-address')}</label>
            <FormInput fieldName='street1' addPadding={true} />
          </SCol>
          <SCol span='6'>
            <label htmlFor='phone'>{t('client.labels.business-phone')}</label>
            <FormInput fieldName='phone' addPadding={true} />
          </SCol>
        </SRow>
        <SRow>
          <SCol span='md-4 6' className='pad-right-sm'>
            <label htmlFor='city'>{t('client.labels.city')}</label>
            <FormInput fieldName='city' addPadding={true} />
          </SCol>
          <SCol span='4'>
            <fieldset style={{ width: '95%' }}>
              <label htmlFor='state'>{t('client.labels.region')}</label>
              <FormSelect fieldName='state' fieldValues={formattedRegions} />
            </fieldset>
          </SCol>
          <SCol span='md-4 2'>
            <label htmlFor='postalCode'>{t('client.labels.postal-code')}</label>
            <FormInput fieldName='postalCode' addPadding={true} />
          </SCol>
        </SRow>
        <SRow>
          <SCol span='6'>
            <fieldset style={{ width: '95%' }}>
              <label htmlFor='country'>{t('client.labels.country')}</label>
              <FormSelect fieldName='country' fieldValues={formattedCountries} />
            </fieldset>
          </SCol>
          {offerFlow === 'ioss' && (
            <SCol span='6'>
              <label htmlFor='vat-registration-number'>{t('client.labels.vat-registration-number-optional')}</label>
              <div className='fs-exclude'>
                {' '}
                {/* Hide VAT registration number in Fullstory */}
                <FormInput fieldName='vatNumber' addPadding={true} />
              </div>
            </SCol>
          )}
        </SRow>
      </form>
      <hr className='margin-top-xs' />
      <span className='inline-flex align-items-center margin-top-lg'>
        <button
          type='button'
          className='primary'
          data-ref='offer.company-button'
          disabled={disabled}
          onClick={handleClick}
        >
          {t('client.buttons.next')}
        </button>
      </span>
    </FormContainer>
  );
};

export default Company;
